<script setup lang="ts">
import { ChevronDown, ChevronRight, Folder, FolderOpen } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import ChannelEdit from '@/components/ChannelEdit.vue'
import { ref, h, type VNode } from 'vue'
import type { Channel } from '@/types/Channels'
import DOMPurify from 'dompurify'

const expandedChannels = ref<Set<number>>(new Set())

interface Props {
	channels: Channel[]
}

const props = defineProps<Props>()

const toggleExpand = (channelId: number) => {
	if (expandedChannels.value.has(channelId)) {
		expandedChannels.value.delete(channelId)
	} else {
		expandedChannels.value.add(channelId)
	}
}

const isExpanded = (channelId: number): boolean => {
	return expandedChannels.value.has(channelId)
}

const getPadding = (depth: number): string => {
	return `${12 + (depth * 24)}px`
}

// Modal state
const isModalOpen = ref(false)
const currentChannel = ref<Channel | null>(null)

const openEditModal = (channel: Channel) => {
	currentChannel.value = channel
	isModalOpen.value = true
}

const handleSave = (savedChannel: Channel) => {
	console.log('Saved channel:', savedChannel)
	isModalOpen.value = false
}

// Recursive render function with proper typing
const renderChannelRow = (channel: Channel, depth = 0): VNode[] => {
	const children: VNode[] = channel.children?.length && isExpanded(channel.id)
		? channel.children.flatMap(child => renderChannelRow(child, depth + 1))
		: []

	return [
		h(TableRow, { class: 'hover:bg-muted/50' }, [
			h(TableCell, { class: 'font-medium', style: { paddingLeft: getPadding(depth) } }, [
				h('div', { class: 'flex items-center gap-2' }, [
					channel.children?.length ? h(Button, {
						variant: 'ghost',
						size: 'icon',
						class: 'h-6 w-6',
						onClick: () => toggleExpand(channel.id)
					}, [
						isExpanded(channel.id) ? h(ChevronDown, { class: 'h-4 w-4' }) : h(ChevronRight, { class: 'h-4 w-4' })
					]) : h('div', { class: 'h-6 w-6' }),
					isExpanded(channel.id) ? h(FolderOpen, { class: 'h-4 w-4 text-primary' }) : h(Folder, { class: 'h-4 w-4 text-primary' }),
					h('span', channel.name)
				])
			]),
			h(TableCell, { class: 'text-muted-foreground' }, channel.shortname),
			h(TableCell, { class: 'text-muted-foreground' }, [
				channel.description ? h('div', { 
					class: 'channel-description',
					innerHTML: DOMPurify.sanitize(channel.description)
				}) : null
			]),
			h(TableCell, [
				h(Badge, {
					variant: channel.status == "1" ? 'default' : 'destructive'
				}, channel.status === "1" ? 'Active' : 'Inactive')
			]),
			h(TableCell, [
				h(Badge, {
					variant: channel.visibility === 'SHOWN' ? 'default' : 'outline'
				}, channel.visibility)
			]),
			h(TableCell, { class: 'text-right' }, [
				h(Button, {
					variant: 'ghost',
					size: 'sm',
					class: 'h-8',
					onClick: () => openEditModal(channel)
				}, 'Manage')
			])
		]),
		...children
	]
}
</script>

<template>
	<div class="rounded-md border">
		<Table>
			<TableHeader>
			<TableRow>
			<TableHead class="w-[300px]">Name</TableHead>
			<TableHead class="w-[300px]">Short Name</TableHead>
			<TableHead>Description</TableHead>
			<TableHead class="w-[120px]">Status</TableHead>
			<TableHead class="w-[120px]">Visibility</TableHead>
			<TableHead class="w-[100px] text-right">Actions</TableHead>
			</TableRow>
			</TableHeader>
			<TableBody>
			  <template v-if="props.channels.length">
				      <template v-for="channel in props.channels" :key="channel.id">
					            <component 
						            v-for="(rowVNode, index) in renderChannelRow(channel)" 
							            :key="`${channel.id}-${index}`"
								            :is="rowVNode"
									          />
						        </template>
							  </template>
			<TableRow v-else>
			<TableCell colspan="6" class="h-24 text-center text-muted-foreground">
			No channels found.
			</TableCell>
			</TableRow>
			</TableBody>
		</Table>
	</div>
	<ChannelEdit v-model:open="isModalOpen" :channel="currentChannel" @save="handleSave" />
</template>

<style scoped>
.channel-description {
	margin-top: 0.5rem;
	padding-left: 1.5rem;
	font-size: 0.9em;
	color: #666;
	line-height: 1.5;
}

.channel-description :deep(p) {
	margin: 0.5em 0;
}

.channel-description :deep(ul),
.channel-description :deep(ol) {
	padding-left: 1.5em;
	margin: 0.5em 0;
}

.channel-description :deep(a) {
	color: #3b82f6;
	text-decoration: underline;
}
</style>
